/**
 * Task evidence panel: list, upload (file picker and drag-and-drop), preview/download via
 * short-lived signed links, and soft delete.
 *
 * Realtime: subscribes to the in-process evidence event bus fed by the app-level SSE
 * connection and reloads the list whenever an event for this task arrives. Toasts for those
 * events are shown at app level (`useSSENotifications`); this component only toasts the HTTP
 * results of actions the user took here, deduplicated against the SSE copy by evidence id.
 *
 * SSE is not relied on alone. It does not stream through the Static Web Apps `/api` proxy
 * (project-management-api#42) and the API's event bus is per worker (#43), so while any row is
 * still scanning the panel also polls the list, and a reconnect of the stream triggers one
 * reload because the events sent in the gap are gone.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, Trash2, Upload } from 'lucide-react';
import Badge, { BadgeVariant } from '../Badge';
import Button from '../Button';
import Card from '../Card';
import {
  deleteEvidence,
  isEvidenceApiError,
  listEvidence,
  precheckEvidenceFile,
  requestEvidenceAccess,
  uploadEvidence,
} from '../../api/evidence';
import {
  notifyEvidence,
  subscribeToEvidenceEvents,
  subscribeToEvidenceResync,
} from '../../services/evidenceEvents';
import type { EvidenceAccessAction, EvidenceRecord, NotificationSeverity } from '../../types/evidence';

export interface EvidencePanelProps {
  taskId: string;
  /** Upload and delete are offered only when the viewer can edit the task's project. */
  canEdit: boolean;
  /** How often to refetch while a row is still scanning. Tests shorten it. */
  pollIntervalMs?: number;
}

interface InFlightUpload {
  id: string;
  filename: string;
  percent: number;
}

const PAGE_SIZE = 50;

/** Scans finish in about 30 s in production; a 12 s poll shows the flip within one cycle of it. */
export const DEFAULT_SCAN_POLL_INTERVAL_MS = 12_000;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatTimestamp = (value?: string | null): string =>
  value ? new Date(value).toLocaleString() : '';

interface StateLabel {
  variant: BadgeVariant;
  label: string;
  detail?: string;
}

/** Map lifecycle + scan state to the badge the contract asks the UI to show. */
export const describeEvidenceState = (item: EvidenceRecord): StateLabel => {
  switch (item.lifecycle_state) {
    case 'pending_scan':
      return { variant: 'warning', label: 'Scanning', detail: 'Preview and download unlock once the scan passes.' };
    case 'active':
      return { variant: 'success', label: 'Available' };
    case 'quarantined':
      return {
        variant: 'danger',
        label: item.scan_status === 'timeout' ? 'Scan timed out' : 'Quarantined',
        detail: item.scan_failure_reason
          ? `Scan failed: ${item.scan_failure_reason}`
          : 'The file did not pass the malware scan and cannot be accessed.',
      };
    case 'soft_deleted':
      return {
        variant: 'default',
        label: 'Deleted',
        detail: item.retention_expires_at
          ? `Binary is purged after ${formatTimestamp(item.retention_expires_at)}.`
          : undefined,
      };
    case 'hard_deleted':
    default:
      return { variant: 'default', label: 'Purged' };
  }
};

/**
 * Severity for a failed action, derived from the error contract. The body carries no severity,
 * so this mirrors what the backend pushes over SSE for the same condition
 * (`_ACCESS_DENIED_NOTIFICATIONS` in the evidence router): a scan still pending is `info`, the
 * other 409s and a 422 are `warning`. `retryable` is not a severity; a retryable conflict is
 * still just a conflict, and rating it `error` made the HTTP toast disagree with its SSE twin.
 */
const severityForError = (status: number, code: string): NotificationSeverity => {
  if (status === 409) return code === 'EVIDENCE_SCAN_PENDING' ? 'info' : 'warning';
  if (status === 422) return 'warning';
  return 'error';
};

/**
 * HTTP error codes whose SSE twin the backend pushes under a different notification code
 * (`_ACCESS_DENIED_NOTIFICATIONS` in the evidence router). The toast for the HTTP result shares
 * the twin's dedupe id so the user sees one toast, not two.
 */
const SSE_NOTIFICATION_ALIAS: Record<string, string> = {
  EVIDENCE_CONFLICT: 'EVIDENCE_ACCESS_DENIED',
};

interface LoadOptions {
  /**
   * A reload behind rows that are already on screen (SSE event, poll, resync, stale-row 404).
   * A failure keeps the rows and toasts instead of replacing them with the error block.
   */
  background?: boolean;
}

/** Reload bookkeeping shared by every trigger, reset when the task changes. */
interface Reloader {
  inFlight: boolean;
  /** A trigger arrived while a reload was running: run exactly one more when it finishes. */
  dirty: boolean;
  dirtyBackground: boolean;
  /** Bumped when the task changes so a response for the previous task is dropped. */
  generation: number;
}

const EvidencePanel: React.FC<EvidencePanelProps> = ({
  taskId,
  canEdit,
  pollIntervalMs = DEFAULT_SCAN_POLL_INTERVAL_MS,
}) => {
  const [items, setItems] = useState<EvidenceRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<InFlightUpload[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mountedRef = useRef(true);
  // Written at every setItems call site, not in a passive effect, so a rejection landing before
  // React flushes effects still sees the rows that just arrived.
  const hasItemsRef = useRef(false);
  const reloaderRef = useRef<Reloader>({ inFlight: false, dirty: false, dirtyBackground: true, generation: 0 });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const replaceItems = useCallback((next: EvidenceRecord[]) => {
    hasItemsRef.current = next.length > 0;
    setItems(next);
  }, []);

  const updateItems = useCallback((update: (current: EvidenceRecord[]) => EvidenceRecord[]) => {
    setItems((current) => {
      const next = update(current);
      hasItemsRef.current = next.length > 0;
      return next;
    });
  }, []);

  /**
   * One reload in flight per task. Every trigger (mount, Retry, SSE event, poll, resync) goes
   * through here; if a reload is already running the request is folded into a single follow-up
   * that starts when the current one finishes. Responses therefore apply in the order they were
   * requested, so a slow early response can never overwrite a fresher list, and an SSE burst of
   * three events costs at most two requests instead of three.
   */
  const load = useCallback(
    async ({ background = false }: LoadOptions = {}) => {
      const reloader = reloaderRef.current;
      if (reloader.inFlight) {
        reloader.dirty = true;
        // A foreground request (Retry) wins over a background one for the follow-up.
        reloader.dirtyBackground = reloader.dirtyBackground && background;
        return;
      }
      reloader.inFlight = true;
      const generation = reloader.generation;
      try {
        const page = await listEvidence(taskId, { limit: PAGE_SIZE });
        if (!mountedRef.current || generation !== reloader.generation) return;
        replaceItems(page.items);
        setNextCursor(page.next_cursor ?? null);
        setLoadError(null);
      } catch (error) {
        if (!mountedRef.current || generation !== reloader.generation) return;
        const message = error instanceof Error ? error.message : 'Failed to load evidence';
        setLoadError(message);
        // A failed background reload must not blank a list that is already on screen; the
        // error block only renders over an empty list, so say so in a toast instead.
        if (background && hasItemsRef.current) {
          notifyEvidence({
            code: 'EVIDENCE_LIST_REFRESH_FAILED',
            message,
            severity: 'error',
            // Several reloads can fail in a row; collapse their failures into one toast.
            dedupeKey: `${taskId}:EVIDENCE_LIST_REFRESH_FAILED`,
          });
        }
      } finally {
        if (mountedRef.current && generation === reloader.generation) setLoading(false);
        reloader.inFlight = false;
        if (reloader.dirty && mountedRef.current && generation === reloader.generation) {
          const followUpBackground = reloader.dirtyBackground;
          reloader.dirty = false;
          reloader.dirtyBackground = true;
          void load({ background: followUpBackground });
        }
      }
    },
    [taskId, replaceItems]
  );

  useEffect(() => {
    const reloader = reloaderRef.current;
    reloader.generation += 1;
    reloader.inFlight = false;
    reloader.dirty = false;
    reloader.dirtyBackground = true;
    setLoading(true);
    load();
  }, [load]);

  // Any evidence event for this task means the list is stale.
  useEffect(
    () =>
      subscribeToEvidenceEvents((event) => {
        if (event.task_id === taskId) load({ background: true });
      }),
    [taskId, load]
  );

  // The stream came back after a drop: whatever it would have told us in the gap is lost.
  useEffect(() => subscribeToEvidenceResync(() => load({ background: true })), [load]);

  // While anything is still scanning, poll. This is what makes Scanning → Available happen on
  // its own when SSE is not delivering (proxy, per-worker bus, or simply a missed event).
  const scanning = items.some((item) => item.lifecycle_state === 'pending_scan');
  useEffect(() => {
    if (!scanning) return undefined;
    const timer = window.setInterval(() => load({ background: true }), pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [scanning, pollIntervalMs, load]);

  const loadMore = async () => {
    if (!nextCursor) return;
    try {
      const page = await listEvidence(taskId, { limit: PAGE_SIZE, cursor: nextCursor });
      updateItems((current) => [...current, ...page.items]);
      setNextCursor(page.next_cursor ?? null);
    } catch (error) {
      reportError(error);
    }
  };

  const reportError = (error: unknown, evidenceId?: string) => {
    if (isEvidenceApiError(error)) {
      notifyEvidence({
        code: SSE_NOTIFICATION_ALIAS[error.code] ?? error.code,
        message: error.message,
        severity: severityForError(error.status, error.code),
        evidenceId,
      });
      // The row we acted on is gone (or was never ours): drop it rather than leave a dead row.
      if (error.status === 404) load({ background: true });
      return;
    }
    notifyEvidence({
      code: 'EVIDENCE_REQUEST_FAILED',
      message: error instanceof Error ? error.message : 'Something went wrong. Please retry.',
      severity: 'error',
    });
  };

  const setBusy = (evidenceId: string, busy: boolean) =>
    setBusyIds((current) => {
      const next = new Set(current);
      if (busy) next.add(evidenceId);
      else next.delete(evidenceId);
      return next;
    });

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || !canEdit) return;
    const selected = Array.from(files);
    await Promise.all(
      selected.map(async (file) => {
        const rejected = precheckEvidenceFile(file);
        if (rejected) {
          notifyEvidence({ ...rejected, severity: 'error' });
          return;
        }
        const uploadId = `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        setUploads((current) => [...current, { id: uploadId, filename: file.name, percent: 0 }]);
        try {
          const result = await uploadEvidence(taskId, file, {
            onProgress: (percent) =>
              setUploads((current) =>
                current.map((entry) => (entry.id === uploadId ? { ...entry, percent } : entry))
              ),
          });
          if (!mountedRef.current) return;
          updateItems((current) =>
            current.some((entry) => entry.id === result.evidence.id)
              ? current
              : [result.evidence, ...current]
          );
          // The row is real even if the list endpoint was down when the panel mounted.
          setLoadError(null);
          notifyEvidence({ ...result.notification, evidenceId: result.evidence.id });
        } catch (error) {
          if (mountedRef.current) reportError(error);
        } finally {
          if (mountedRef.current) {
            setUploads((current) => current.filter((entry) => entry.id !== uploadId));
          }
        }
      })
    );
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
    // Allow re-selecting the same file.
    event.target.value = '';
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!canEdit) return;
    event.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => setDragActive(false);

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!canEdit) return;
    event.preventDefault();
    setDragActive(false);
    handleFiles(event.dataTransfer?.files ?? null);
  };

  const handleAccess = async (item: EvidenceRecord, action: EvidenceAccessAction) => {
    setBusy(item.id, true);
    try {
      // Links are minted on every click and never cached: expiry is enforced by the storage
      // backend, so a fresh request is the only reliable "refresh on expiry".
      const grant = await requestEvidenceAccess(taskId, item.id, action);
      window.open(grant.signed_url, '_blank', 'noopener,noreferrer');
      if (grant.notification) {
        notifyEvidence({ ...grant.notification, evidenceId: item.id });
      }
    } catch (error) {
      reportError(error, item.id);
    } finally {
      if (mountedRef.current) setBusy(item.id, false);
    }
  };

  const handleDelete = async (item: EvidenceRecord) => {
    if (!window.confirm(`Delete "${item.filename}"? The file is kept for the retention period and then purged.`)) {
      return;
    }
    setBusy(item.id, true);
    try {
      const result = await deleteEvidence(taskId, item.id);
      updateItems((current) => current.filter((entry) => entry.id !== item.id));
      notifyEvidence({ ...result.notification, evidenceId: item.id });
    } catch (error) {
      reportError(error, item.id);
    } finally {
      if (mountedRef.current) setBusy(item.id, false);
    }
  };

  const canDelete = (item: EvidenceRecord) =>
    canEdit && ['pending_scan', 'active', 'quarantined'].includes(item.lifecycle_state);

  return (
    <Card variant="elevated">
      <Card.Header
        title={`Evidence (${items.length})`}
        subtitle="Files are scanned before they can be previewed or downloaded."
        actions={
          canEdit ? (
            <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-1" aria-hidden="true" />
              Upload evidence
            </Button>
          ) : undefined
        }
      />
      <Card.Body>
        {canEdit && (
          <div
            role="region"
            aria-label="Evidence drop zone"
            data-testid="evidence-drop-zone"
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`mb-4 rounded-lg border-2 border-dashed p-4 text-center text-sm transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
            }`}
          >
            <p>
              Drag and drop files here, or{' '}
              <label
                htmlFor={`evidence-file-input-${taskId}`}
                className="cursor-pointer font-medium text-blue-600 dark:text-blue-400 underline"
              >
                choose files
              </label>
              .
            </p>
            <p className="mt-1 text-xs">PDF, PNG, JPEG, plain text, Word, Excel or PowerPoint documents up to 25 MB.</p>
            <input
              id={`evidence-file-input-${taskId}`}
              ref={fileInputRef}
              type="file"
              multiple
              className="sr-only"
              aria-label="Upload evidence files"
              onChange={onInputChange}
            />
          </div>
        )}

        {uploads.length > 0 && (
          <ul className="mb-4 space-y-2" aria-label="Uploads in progress">
            {uploads.map((upload) => (
              <li key={upload.id} className="text-sm">
                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                  <span className="truncate">Uploading {upload.filename}</span>
                  <span>{upload.percent}%</span>
                </div>
                <div
                  role="progressbar"
                  aria-label={`Upload progress for ${upload.filename}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={upload.percent}
                  className="mt-1 h-2 w-full rounded bg-gray-200 dark:bg-gray-700"
                >
                  <div className="h-2 rounded bg-blue-500" style={{ width: `${upload.percent}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}

        {loading ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading evidence…</p>
        ) : loadError && items.length === 0 ? (
          <div className="text-sm text-red-700 dark:text-red-400">
            <p>{loadError}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => load()}>
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No evidence has been attached to this task yet.
          </p>
        ) : (
          <ul className="space-y-2" aria-label="Evidence files">
            {items.map((item) => {
              const state = describeEvidenceState(item);
              const isActive = item.lifecycle_state === 'active';
              const busy = busyIds.has(item.id);
              return (
                <li
                  key={item.id}
                  data-testid={`evidence-row-${item.id}`}
                  className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50"
                >
                  <FileText className="h-5 w-5 flex-shrink-0 text-gray-500" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium text-gray-900 dark:text-white">{item.filename}</span>
                      <Badge variant={state.variant} size="sm">
                        {state.label}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      {formatBytes(item.size_bytes)} · {item.content_type} · uploaded {formatTimestamp(item.uploaded_at)}
                    </div>
                    {state.detail && (
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">{state.detail}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isActive && item.preview_supported && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        aria-label={`Preview ${item.filename}`}
                        onClick={() => handleAccess(item, 'preview')}
                      >
                        <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
                        Preview
                      </Button>
                    )}
                    {isActive && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        aria-label={`Download ${item.filename}`}
                        onClick={() => handleAccess(item, 'download')}
                      >
                        <Download className="mr-1 h-4 w-4" aria-hidden="true" />
                        Download
                      </Button>
                    )}
                    {canDelete(item) && (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={busy}
                        aria-label={`Delete ${item.filename}`}
                        onClick={() => handleDelete(item)}
                      >
                        <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" />
                        Delete
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {nextCursor && !loading && (
          <div className="mt-3 text-center">
            <Button variant="outline" size="sm" onClick={loadMore}>
              Load more
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default EvidencePanel;
